// Angular Modules
// import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
// import { BrowserModule } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';

// Shared Modules
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatNativeDateModule, MatOptionModule, MatRippleModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { L10nTranslationModule } from 'angular-l10n';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { GoogleMapsModule } from '@angular/google-maps';

// Misc
import { DataChartModule } from './data-chart/data-chart.module';
import { AutoResizeTextareaDirective } from './formatting/auto-resize-textarea.directive';
import { HtmlHelper } from './html-helper/html-helper';
import { FixDatePipe } from './pipes/fix-date/fix-date.pipe';
import { FriendlyStringPipe } from './pipes/friendly-string/friendly-string.pipe';
import { LimitTextPipe } from './pipes/limit-text/limit-text.pipe';
import { PhoneFormatterPipe } from './pipes/phone-formatter/phone-formatter.pipe';
import { XmlPipe } from './pipes/xml/xml.pipe';
import {
  ScrollSpyContentDirective,
  ScrollSpyMenuDirective,
  ScrollSpyMenuItemDirective,
} from './scroll-spy/scroll-spy.directive';
import { NoWhitespaceDirective } from './validators/NoWhitespaceDirective/no-whitespace.directive';

// Shared Components
import { LayoutModule } from '@angular/cdk/layout';
import { DomSanitizer } from '@angular/platform-browser';
import { CKEditorModule } from 'ckeditor4-angular';
import { SelectorRowComponent } from 'src/app/shared/selector-row/selector-row.component';
import { SelectAssetAttributesComponent } from '../associations/select-asset-attributes/select-asset-attributes.component';
import { SelectAssociationValuesComponent } from '../associations/select-association-values/select-association-values.component';
import { GroupsTreeComponent } from '../groups/groups-tree/groups-tree.component';
import { IncidentSearchTypeComponent } from '../incident-v2/search-type/search-type.component';
import { IncidentSelectTypeComponent } from '../incident-v2/select-type/select-type.component';
import { SurveysSearchComponent } from '../surveys/surveys-search/surveys-search.component';
import { AlertFormService } from '../tenant/alert-form.service';
import { AdminFormFieldComponent } from './admin-form-field/admin-form-field.component';
import { AdminFormComponent } from './admin-form/admin-form.component';
import { AdminSelectDialogComponent } from './admin-select-dialog/admin-select-dialog.component';
import { AlertDefaultInfoComponent } from './alert-default-info/alert-default-info.component';
import { AlertDetailIconsComponent } from './alert-detail-icons/alert-detail-icons.component';
import { ApiSelectorComponent } from './api-selector/api-selector.component';
import { AppVersionService } from './app-version.service';
import { AsMatSidenavComponent } from './as-mat-sidenav/as-mat-sidenav.component';
import { AbacAuthorizationComponent } from './authorization/abac-authorization/abac-authorization.component';
import { AddGroupAuthorizationComponent } from './authorization/add-group-authorization/add-group-authorization.component';
import { AddRoleAuthorizationComponent } from './authorization/add-role-authorization/add-role-authorization.component';
import { AuthorizationComponent } from './authorization/authorization.component';
import { BalsaCardComponent } from './balsa-card/balsa-card.component';
import { BaseHref } from './base-href.helper';
import { ContactsFormFieldComponent } from './contacts-form-field/contacts-form-field.component';
import { ContactsSelectDialogComponent } from './contacts-select-dialog/contacts-select-dialog.component';
import { ContractLimitAddDialogComponent } from './contracts/contract-limit-add-dialog/contract-limit-add-dialog.component';
import { ContractLimitOverviewComponent } from './contracts/contract-limit-overview/contract-limit-overview.component';
import { ContractListViewComponent } from './contracts/contract-list-view/contract-list-view.component';
import { ContractsOverageListViewComponent } from './contracts/contracts-overage-list-view/contracts-overage-list-view.component';
import { ContractViewComponent } from './contracts/contract-view/contract-view.component';
import { CustomizeColumnsDialogComponent } from './data-table/customize-columns-dialog/customize-columns-dialog.component';
import { DataTableComponent } from './data-table/data-table.component';
import { EllipsisPipe } from './ellipsis/ellipsis.pipe';
import { ErrorMessageComponent } from './error-message/error-message.component';
import { SelectFacilitiesComponent } from './facility/select-facilities/select-facilities.component';
import { FeedbackComponent } from './feedback/feedback.component';
import { DateFilterComponent } from './filtered-search/filtered-search-option/date-filter/date-filter.component';
import { DateRangeFilterComponent } from './filtered-search/filtered-search-option/date-range-filter/date-range-filter.component';
import { DynamicSelectionFilterComponent } from './filtered-search/filtered-search-option/dynamic-selection-filter/dynamic-selection-filter.component';
import { FilteredSearchOptionComponent } from './filtered-search/filtered-search-option/filtered-search-option.component';
import { SelectionFilterComponent } from './filtered-search/filtered-search-option/selection-filter/selection-filter.component';
import { SlideToggleFilterComponent } from './filtered-search/filtered-search-option/slide-toggle-filter/slide-toggle-filter.component';
import { FilteredSearchComponent } from './filtered-search/filtered-search.component';
import { FixedSaveHeaderComponent } from './fixed-save-header/fixed-save-header.component';
import { GroupPathTreeComponent } from './group-path-tree/group-path-tree.component';
import { HickorySinglePageComponent } from './hickory-single-page/hickory-single-page.component';
import { ImpersonationComponent } from './impersonation/impersonation.component';
import { InfoCardComponent } from './info-card/info-card.component';
import { JsonformsAdminIdRendererComponent } from './jsonforms/jsonforms-admin-id-renderer/jsonforms-admin-id-renderer.component';
import { BannerComponent } from './layout/banner/banner.component';
import { LayoutComponent } from './layout/layout.component';
import { NavElementComponent } from './layout/nav-element/nav-element.component';
import { LoadingComponent } from './loading/loading.component';
import { LongPressDirective } from './long-press/long-press.directive';
import { MagicInputComponent } from './magic-input/magic-input.component';
import { MemberExportDialogComponent } from './member-export-dialog/member-export-dialog.component';
import { MockSelectorComponent } from './mock-selector/mock-selector.component';
import { ConfirmComponent } from './modals/confirm/confirm.component';
import { ErrorComponent } from './modals/error/error.component';
import { OkMessageComponent } from './modals/ok-message/ok-message.component';
import { CapitalizePipe } from './pipes/capitalize/capitalize.pipe';
import { MaxItemsPipe } from './pipes/max-items/max-items.pipe';
import { RichContentComponent } from './rich-content/rich-content.component';
import { SelectGroupsComponent } from './select-groups/select-groups.component';
import { SelectUsersComponent } from './select-users/select-users.component';
import { SuggestSearchBoxComponent } from './suggest-search-box/suggest-search-box.component';
import { TabRouterComponent } from './tab-router/tab-router.component';
import { AvailableTemplatesDialogComponent } from './templated-input/available-templates-dialog/available-templates-dialog.component';
import { TemplatedInputInstructionsComponent } from './templated-input/templated-input-instructions/templated-input-instructions.component';
import { TemplatedInputPreviewComponent } from './templated-input/templated-input-preview/templated-input-preview.component';
import { TemplatedSimpleInputComponent } from './templated-input/templated-simple-input/templated-simple-input.component';
import { TenantSearchComponent } from './tenant-search/tenant-search.component';
import { UserGroupsComponent } from './user-groups/user-groups.component';
import { ImportExportWidgetBaseComponent } from './widgets/import-export/import-export-widget-base/import-export-widget-base.component';
import { ImportExportWidgetListViewComponent } from './widgets/import-export/import-export-widget-list-view/import-export-widget-list-view.component';
import { TenantSelectionComponent } from './widgets/tenant-selection/tenant-selection.component';
import { ProgressBarComponent } from './widgets/progress-bar/progress-bar.component';
import { MaxCharactersPipe } from './pipes/max-characters/max-characters.pipe';
import { SimpleTableDialogComponent } from './simple-table-dialog/simple-table-dialog.component';
import { AdvancedFilterSelectionComponent } from './advanced-filter-selection/advanced-filter-selection.component';
import { GroupsFormFieldComponent } from './groups-form-field/groups-form-field.component';
import { GroupsSelectDialogComponent } from './groups-select-dialog/groups-select-dialog.component';
import { SelectFromListDialogComponent } from './modals/select-from-list-dialog/select-from-list-dialog.component';
import { CustomDateDialogComponent } from './custom-date-dialog/custom-date-dialog.component';
import { RolesSelectComponentComponent } from './roles-select-component/roles-select-component.component';
import { CardWrapperComponent } from './balsa-card/card-wrapper/card-wrapper.component';
import { FacilitiesMapComponent } from './map/facilities-map/facilities-map.component';
import { AddFacilitiesCsvDialogComponent } from './facility/add-facilities-csv-dialog/add-facilities-csv-dialog.component';
import { PhonesDetailModalComponent } from './phones-detail-modal/phones-detail-modal.component';
import { DragDropDirective } from './drag-drop/drag-drop.directive';
import { RecipientSettingsComponent } from '../alert/alert-details/components/recipient-details/recipient-settings/recipient-settings.component';
import { GroupDetailsDialogComponent } from '../alert/alert-details/components/recipient-details/group-details-dialog/group-details-dialog.component';
import { ContactDetailsDialogComponent } from '../alert/alert-details/components/recipient-details/contact-details-dialog/contact-details-dialog.component';
import { RecipientDetailsCitiesComponent } from '../alert/alert-details/components/recipient-details/recipient-details-cities/recipient-details-cities.component';
import { RecipientDetailsAreasComponent } from '../alert/alert-details/components/recipient-details/recipient-details-areas/recipient-details-areas.component';
import { RecipientDetailsAdhocComponent } from '../alert/alert-details/components/recipient-details/recipient-details-adhoc/recipient-details-adhoc.component';

@NgModule({
  imports: [
    CKEditorModule,
    // System Modules
    RouterModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    // Shared Modules
    L10nTranslationModule,
    DragDropModule,
    DataChartModule,
    // Material Modules
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatTableModule,
    MatTabsModule,
    MatSortModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatOptionModule,
    MatSelectModule,
    MatMenuModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatRadioModule,
    MatTooltipModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDividerModule,
    MatListModule,
    MatCardModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatGridListModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatSidenavModule,
    MatSnackBarModule,
    MatToolbarModule,
    MatTreeModule,
    MatRippleModule,
    MatBadgeModule,
    MatStepperModule,
    LayoutModule,
    ScrollingModule,
    // Standalone
    LongPressDirective,
    CardWrapperComponent,
    FixDatePipe,
    // NgxMask
    NgxMaskDirective,
    NgxMaskPipe,
    GoogleMapsModule,
  ],
  declarations: [
    // Miscellaneous
    AutoResizeTextareaDirective,
    NoWhitespaceDirective,
    ScrollSpyContentDirective,
    ScrollSpyMenuDirective,
    ScrollSpyMenuItemDirective,
    DragDropDirective,
    PhoneFormatterPipe,
    LimitTextPipe,
    CapitalizePipe,
    EllipsisPipe,
    FriendlyStringPipe,
    XmlPipe,
    MaxItemsPipe,
    MaxCharactersPipe,
    // Shared Components
    AlertDetailIconsComponent,
    ApiSelectorComponent,
    BalsaCardComponent,
    BannerComponent,
    ConfirmComponent,
    CustomizeColumnsDialogComponent,
    DataTableComponent,
    ErrorComponent,
    FeedbackComponent,
    FilteredSearchComponent,
    FilteredSearchOptionComponent,
    FixedSaveHeaderComponent,
    GroupPathTreeComponent,
    GroupsTreeComponent,
    HickorySinglePageComponent,
    ImpersonationComponent,
    ImportExportWidgetBaseComponent,
    IncidentSearchTypeComponent,
    IncidentSelectTypeComponent,
    LayoutComponent,
    LoadingComponent,
    MemberExportDialogComponent,
    MockSelectorComponent,
    NavElementComponent,
    OkMessageComponent,
    ProgressBarComponent,
    RichContentComponent,
    SelectAssetAttributesComponent,
    SelectAssociationValuesComponent,
    SelectFacilitiesComponent,
    SelectFromListDialogComponent,
    SelectGroupsComponent,
    SelectorRowComponent,
    SelectUsersComponent,
    SurveysSearchComponent,
    TabRouterComponent,
    TenantSelectionComponent,
    TemplatedSimpleInputComponent,
    TemplatedInputInstructionsComponent,
    TemplatedInputPreviewComponent,
    AvailableTemplatesDialogComponent,
    ImportExportWidgetListViewComponent,
    ErrorMessageComponent,
    AsMatSidenavComponent,
    AlertDefaultInfoComponent,
    InfoCardComponent,
    SuggestSearchBoxComponent,
    TenantSearchComponent,
    MagicInputComponent,
    ContractLimitAddDialogComponent,
    ContractListViewComponent,
    ContractsOverageListViewComponent,
    ContractViewComponent,
    ContractLimitOverviewComponent,
    ContactsSelectDialogComponent,
    RolesSelectComponentComponent,
    AuthorizationComponent,
    AbacAuthorizationComponent,
    UserGroupsComponent,
    AddGroupAuthorizationComponent,
    AddRoleAuthorizationComponent,
    ContactsFormFieldComponent,
    GroupsFormFieldComponent,
    GroupsSelectDialogComponent,
    AdminFormFieldComponent,
    AdminFormComponent,
    AdminSelectDialogComponent,
    FacilitiesMapComponent,
    AddFacilitiesCsvDialogComponent,
    RecipientSettingsComponent,
    GroupDetailsDialogComponent,
    ContactDetailsDialogComponent,
    RecipientDetailsCitiesComponent,
    RecipientDetailsAreasComponent,
    RecipientDetailsAdhocComponent,

    // JSON Forms
    JsonformsAdminIdRendererComponent,

    // Filters
    DateFilterComponent,
    DateRangeFilterComponent,
    SlideToggleFilterComponent,
    SelectionFilterComponent,
    DynamicSelectionFilterComponent,
    SimpleTableDialogComponent,
    AdvancedFilterSelectionComponent,
    CustomDateDialogComponent,
    PhonesDetailModalComponent,
  ],
  exports: [
    // System Modules
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    RouterModule,
    // Shared Modules
    L10nTranslationModule,
    DragDropModule,
    DataChartModule,
    // Material Modules
    MatButtonModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatTableModule,
    MatTabsModule,
    MatSortModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatChipsModule,
    MatIconModule,
    MatOptionModule,
    MatPaginatorModule,
    MatSelectModule,
    MatMenuModule,
    MatAutocompleteModule,
    MatDialogModule,
    MatRadioModule,
    MatTooltipModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatDividerModule,
    MatListModule,
    MatCardModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatGridListModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatSidenavModule,
    MatSnackBarModule,
    MatToolbarModule,
    MatTreeModule,
    MatRippleModule,
    MatBadgeModule,
    MatStepperModule,
    ScrollingModule,
    // NgxMask
    NgxMaskDirective,
    NgxMaskPipe,
    // Miscellaneous
    AutoResizeTextareaDirective,
    NoWhitespaceDirective,
    ScrollSpyContentDirective,
    ScrollSpyMenuDirective,
    ScrollSpyMenuItemDirective,
    DragDropDirective,
    PhoneFormatterPipe,
    FixDatePipe,
    LimitTextPipe,
    CapitalizePipe,
    EllipsisPipe,
    FriendlyStringPipe,
    XmlPipe,
    MaxItemsPipe,
    MaxCharactersPipe,
    // Shared Components
    AlertDetailIconsComponent,
    AsMatSidenavComponent,
    BalsaCardComponent,
    BannerComponent,
    ConfirmComponent,
    DataTableComponent,
    ErrorComponent,
    FilteredSearchComponent,
    FilteredSearchOptionComponent,
    FixedSaveHeaderComponent,
    GroupPathTreeComponent,
    GroupsTreeComponent,
    HickorySinglePageComponent,
    IncidentSearchTypeComponent,
    IncidentSelectTypeComponent,
    InfoCardComponent,
    LayoutComponent,
    LoadingComponent,
    NavElementComponent,
    OkMessageComponent,
    ProgressBarComponent,
    RichContentComponent,
    SelectAssetAttributesComponent,
    SelectAssociationValuesComponent,
    SelectFacilitiesComponent,
    SelectFromListDialogComponent,
    SelectGroupsComponent,
    SelectorRowComponent,
    SelectUsersComponent,
    SurveysSearchComponent,
    TabRouterComponent,
    TenantSelectionComponent,
    TemplatedSimpleInputComponent,
    TemplatedInputInstructionsComponent,
    TemplatedInputPreviewComponent,
    AvailableTemplatesDialogComponent,
    ErrorMessageComponent,
    SuggestSearchBoxComponent,
    TenantSearchComponent,
    MagicInputComponent,
    ContactsSelectDialogComponent,
    RolesSelectComponentComponent,
    ContractLimitAddDialogComponent,
    ContractListViewComponent,
    ContractsOverageListViewComponent,
    ContractViewComponent,
    ContractLimitOverviewComponent,
    AuthorizationComponent,
    AbacAuthorizationComponent,
    UserGroupsComponent,
    AddGroupAuthorizationComponent,
    AddRoleAuthorizationComponent,
    ContactsFormFieldComponent,
    GroupsFormFieldComponent,
    GroupsSelectDialogComponent,
    AdminFormFieldComponent,
    AdminFormComponent,
    AdvancedFilterSelectionComponent,
    FacilitiesMapComponent,
    AddFacilitiesCsvDialogComponent,
    // JSON Forms
    JsonformsAdminIdRendererComponent,
    CardWrapperComponent,
    PhonesDetailModalComponent,
    RecipientSettingsComponent,
    GroupDetailsDialogComponent,
    ContactDetailsDialogComponent,
    RecipientDetailsCitiesComponent,
    RecipientDetailsAreasComponent,
    RecipientDetailsAdhocComponent,
  ],
  providers: [
    // NOTE: services provided here will NOT be singleton -- for singletons, add to the AppModule instead
    HtmlHelper,
    BaseHref,
    AlertFormService,
    AppVersionService,
    provideNgxMask(),
  ],
})
export class SharedModule {
  constructor(iconRegistry: MatIconRegistry, sanitizer: DomSanitizer) {
    const iconPath = '/resources/images/icons';
    iconRegistry.addSvgIcon(
      'send_alert_red',
      sanitizer.bypassSecurityTrustResourceUrl(`${iconPath}/send_alert_red.svg`),
    );
    iconRegistry.addSvgIcon('send_alert', sanitizer.bypassSecurityTrustResourceUrl(`${iconPath}/send_alert.svg`));
    iconRegistry.addSvgIcon('send_poll', sanitizer.bypassSecurityTrustResourceUrl(`${iconPath}/send_poll.svg`));
    iconRegistry.addSvgIcon(
      'microsoft_teams',
      sanitizer.bypassSecurityTrustResourceUrl(`${iconPath}/microsoft_teams.svg`),
    );
    iconRegistry.addSvgIcon('konexus_icon', sanitizer.bypassSecurityTrustResourceUrl(`${iconPath}/konexus_icon.svg`));
  }
}
